// src/components/hotel/BookingForm.jsx
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaTimes } from 'react-icons/fa';
import { createBooking } from '../../../../api/booking';
import styles from './css/BookingForm.module.css';
import Payment from '../../../payments/Payment';
import OneNightPay from '../../../payments/OneNightPay';

const BookingForm = ({ hotel, room, priceRange, onClose }) => {
    const { t } = useTranslation();
    const [formData, setFormData] = useState({
        checkInDate: '',
        checkOutDate: '',
        guests: 1,
        specialRequests: ''
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [dateError, setDateError] = useState('');

    const [OneNightPayModal, setOneNightPayModal] = useState(false);
    const [paymentModal, setPaymentModal] = useState(false);
    const navigate = useNavigate();

    // Check if room is available
    const isRoomAvailable = room && room.numAvailable > 0;

    // Calculate minimum check-out date (check-in date + 1 day)
    const getMinCheckOutDate = () => {
        if (!formData.checkInDate) return new Date().toISOString().split('T')[0];

        const checkInDate = new Date(formData.checkInDate);
        const nextDay = new Date(checkInDate);
        nextDay.setDate(nextDay.getDate() + 1);
        return nextDay.toISOString().split('T')[0];
    };

    // Validate dates when they change
    const validateDates = () => {
        if (formData.checkInDate && formData.checkOutDate) {
            const checkIn = new Date(formData.checkInDate);
            const checkOut = new Date(formData.checkOutDate);

            if (checkOut <= checkIn) {
                return false;
            }
        }
        return true;
    };

    useEffect(() => {
        if (formData.checkInDate && formData.checkOutDate && !validateDates()) {
            setDateError('Check-out date must be after check-in date');
        } else {
            setDateError('');
        }
    }, [formData.checkInDate, formData.checkOutDate]);

    const handleChange = (e) => {
        const { name, value } = e.target;

        setFormData(prev => {
            const newFormData = {
                ...prev,
                [name]: value
            };

            // If check-in date changes and check-out date is invalid, clear check-out
            if (name === 'checkInDate' && prev.checkOutDate) {
                const newCheckIn = new Date(value);
                const currentCheckOut = new Date(prev.checkOutDate);

                if (currentCheckOut <= newCheckIn) {
                    newFormData.checkOutDate = '';
                }
            }

            return newFormData;
        });
    };


    const handleSubmit = async (number, medium, type) => {

        console.log('Phone Number: ', number);
        if (!isRoomAvailable) return;

        // Validate dates before submission
        if (!validateDates()) {
            setError('Please select valid dates');
            return;
        }

        setLoading(true);
        setError(null);

        if(!number){
            return;
        }

        try {
            console.log('Hotel ID', hotel._id);
            const response = await createBooking(hotel._id, room._id, formData, number, medium, type);

            if (response.success) {
                navigate('/user/bookings', {
                    state: {
                        bookingSuccess: true,
                        bookingId: response.booking._id
                    }
                });
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const calculateTotal = () => {
        if (!isRoomAvailable) return 0;
        if (formData.checkInDate && formData.checkOutDate && validateDates()) {
            const checkIn = new Date(formData.checkInDate);
            const checkOut = new Date(formData.checkOutDate);
            const nights = Math.ceil((checkOut - checkIn) / (1000 * 60 * 60 * 24));
            return nights * room.pricePerNight;
        }
        return 0;
    };

    const calculateNights = () => {
        if (!isRoomAvailable) return 0;
        if (formData.checkInDate && formData.checkOutDate && validateDates()) {
            const checkIn = new Date(formData.checkInDate);
            const checkOut = new Date(formData.checkOutDate);
            return Math.ceil((checkOut - checkIn) / (1000 * 60 * 60 * 24));
        }
        return 0;
    };

    // Check if booking is valid
    const isBookingValid = () => {
        return formData.checkInDate &&
               formData.checkOutDate &&
               validateDates() &&
               formData.guests;
    };

    const showPriceBreakdown = formData.checkInDate && formData.checkOutDate && validateDates();

    return (
        <>
        {paymentModal && isRoomAvailable && createPortal(
            <Payment
                onClose={() => setPaymentModal(false)}
                amounts={room.bookingFee}
                handleSubmit={handleSubmit}
                // handleCommission={commissionSplit}
                // hotel={true}
            />,
            document.body
        )}

        {OneNightPayModal && isRoomAvailable && createPortal(
            <OneNightPay
                onClose={() => setOneNightPayModal(false)}
                amount={room.pricePerNight}
                handleSubmit={handleSubmit}
            />,
            document.body
        )}

        {createPortal(
        <div className={styles.bookingFormModal} onClick={onClose}>
            <div className={styles.bookingFormContainer} onClick={(e) => e.stopPropagation()}>
                {/* ===== Header ===== */}
                <div className={styles.bookingHeader}>
                    <div className={styles.bookingHeaderTop}>
                        <div className={styles.roomBadge}>
                            <i className="fa fa-bed" />
                        </div>
                        <div className={styles.roomInfo}>
                            <h3 className={styles.roomType}>{room.roomType} {t('hotel_booking.room', 'Room')}</h3>
                            <span className={styles.roomMeta}>
                                <i className="fa fa-user-o" /> {t('hotel_booking.up_to', 'Up to')} {room.capacity} {t('hotel_booking.guest', 'guest')}{room.capacity > 1 ? 's' : ''}
                            </span>
                        </div>
                        <button onClick={onClose} className={styles.closeButton} aria-label="Close">
                            <FaTimes />
                        </button>
                    </div>
                    <div className={styles.headerPriceRow}>
                        <div className={styles.priceDisplay}>
                            <span className={styles.priceAmount}>XAF {room.pricePerNight.toLocaleString()}</span>
                            <span className={styles.pricePerNight}>/ night</span>
                        </div>
                        {isRoomAvailable ? (                                <span className={styles.availabilityPill}>
                                    <i className="fa fa-check-circle" /> {room.numAvailable} {t('hotel_booking.left', 'left')}
                                </span>
                        ) : (                                <span className={`${styles.availabilityPill} ${styles.availabilityPillFull}`}>
                                    <i className="fa fa-times-circle" /> {t('hotel_booking.fully_booked', 'Fully booked')}
                                </span>
                        )}
                    </div>
                </div>

                {isRoomAvailable ? (
                    <>
                    {/* ===== Body (scrollable) ===== */}
                    <div className={styles.bookingBody}>
                        <form className={styles.bookingForm}>
                            {/* Dates */}
                            <div className={styles.formSection}>
                                <div className={styles.sectionLabel}>
                                    <i className="fa fa-calendar" /> {t('hotel_booking.select_dates', 'Select your dates')}
                                </div>
                                <div className={styles.dateGrid}>
                                    <div className={styles.formGroup}>
                                        <label className={styles.formLabel} htmlFor="checkInDate">{t('hotel_booking.check_in', 'Check-in')}</label>
                                        <div className={styles.inputWrap}>
                                            <i className="fa fa-sign-in" aria-hidden="true" />
                                            <input
                                                type="date"
                                                id="checkInDate"
                                                name="checkInDate"
                                                value={formData.checkInDate}
                                                onChange={handleChange}
                                                required
                                                min={new Date().toISOString().split('T')[0]}
                                                className={styles.formInput}
                                            />
                                        </div>
                                    </div>
                                    <div className={styles.formGroup}>
                                        <label className={styles.formLabel} htmlFor="checkOutDate">{t('hotel_booking.check_out', 'Check-out')}</label>
                                        <div className={styles.inputWrap}>
                                            <i className="fa fa-sign-out" aria-hidden="true" />
                                            <input
                                                type="date"
                                                id="checkOutDate"
                                                name="checkOutDate"
                                                value={formData.checkOutDate}
                                                onChange={handleChange}
                                                required
                                                min={getMinCheckOutDate()}
                                                className={styles.formInput}
                                            />
                                        </div>
                                    </div>
                                </div>
                                {dateError && (
                                    <div className={styles.dateError}>
                                        <i className="fa fa-exclamation-triangle" />
                                        {dateError}
                                    </div>
                                )}
                            </div>

                            {/* Guests */}
                            <div className={styles.formSection}>
                                <div className={styles.sectionLabel}>
                                    <i className="fa fa-users" /> {t('hotel_booking.guests', 'Guests')}
                                </div>
                                <div className={styles.formGroup}>
                                    <select
                                        name="guests"
                                        value={formData.guests}
                                        onChange={handleChange}
                                        required
                                        className={styles.formSelect}
                                    >
                                        {[...Array(room.capacity).keys()].map(num => (
                                            <option key={num + 1} value={num + 1}>
                                                {num + 1} {t('hotel_booking.guest', 'Guest')}{num > 0 ? 's' : ''}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {/* Special requests */}
                            <div className={styles.formSection}>
                                <div className={styles.sectionLabel}>
                                    <i className="fa fa-comment" /> {t('hotel_booking.special_requests', 'Special requests')} <span className={styles.optionalTag}>{t('hotel_booking.optional', 'optional')}</span>
                                </div>
                                <textarea
                                    name="specialRequests"
                                    value={formData.specialRequests}
                                    onChange={handleChange}
                                    rows="2"
                                    placeholder={t('hotel_booking.special_requests_placeholder', 'Any special requirements or preferences?')}
                                    className={styles.formTextarea}
                                />
                            </div>

                            {/* Price breakdown */}
                            {showPriceBreakdown && (
                                <div className={styles.priceSummary}>
                                    <div className={styles.summaryRow}>
                                        <span className={styles.itemLabel}>XAF {room.pricePerNight.toLocaleString()} × {calculateNights()} night{calculateNights() > 1 ? 's' : ''}</span>
                                        <span className={styles.itemValue}>XAF {(room.pricePerNight * calculateNights()).toLocaleString()}</span>
                                    </div>
                                    <div className={styles.summaryDivider} />
                                    <div className={styles.summaryRow}>
                                        <span className={styles.totalLabel}>{t('hotel_booking.total', 'Total')}</span>
                                        <span className={styles.totalAmount}>XAF {calculateTotal().toLocaleString()}</span>
                                    </div>
                                </div>
                            )}

                            {error && (
                                <div className={styles.errorMessage}>
                                    <i className="fa fa-exclamation-triangle" />
                                    {error}
                                </div>
                            )}
                        </form>
                    </div>

                    {/* ===== Sticky footer ===== */}
                    <div className={styles.bookingFooter}>
                        <div className={styles.footerTotal}>
                            <span className={styles.footerTotalLabel}>
                                {showPriceBreakdown ? t('hotel_booking.total', 'Total') : t('hotel_booking.from', 'From')}
                            </span>
                            <span className={styles.footerTotalAmount}>
                                XAF {showPriceBreakdown
                                    ? calculateTotal().toLocaleString()
                                    : room.pricePerNight.toLocaleString()}
                            </span>
                        </div>
                        {!hotel.allowOneNightPaymentOnly ? (
                            <button
                                type="button"
                                className={styles.bookButton}
                                disabled={loading || !isBookingValid()}
                                onClick={() => {
                                    if (validateDates()) {
                                        // setPaymentModal(true);
                                        handleSubmit('0000', 'none', 'booking');
                                    }
                                }}
                            >
                                {loading ? (
                                    <>
                                        <span className={styles.loadingSpinner}></span>
                                        Processing...
                                    </>
                                ) : (
                                    <>
                                        {t('hotel_booking.book_now', 'Book Now')}
                                        <i className={`fa fa-arrow-right ${styles.buttonArrow}`} />
                                    </>
                                )}
                            </button>
                        ) : (
                            <button
                                type="button"
                                className={styles.bookButton}
                                disabled={!isBookingValid()}
                                onClick={() => {
                                    if (validateDates()) {
                                        setOneNightPayModal(true);
                                    }
                                }}
                            >                                        {t('hotel_booking.one_night_pay', 'One Night Pay')}
                                        <i className={`fa fa-arrow-right ${styles.buttonArrow}`} />
                            </button>
                        )}
                    </div>
                    </>
                ) : (                        <div className={styles.unavailableContainer}>
                        <div className={styles.unavailableIcon}><i className="fa fa-ban" /></div>
                        <h4 className={styles.unavailableTitle}>{t('hotel_booking.room_unavailable', 'Room Unavailable')}</h4>
                        <p className={styles.unavailableMessage}>
                            {t('hotel_booking.no_rooms_available', 'No rooms in this category are available at the moment.')}
                            <br />
                            {t('hotel_booking.select_another_room', 'Please select another room type.')}
                        </p>
                        <button
                            className={styles.selectAnotherButton}
                            onClick={onClose}
                        >
                            <i className="fa fa-arrow-left" />
                            {t('hotel_booking.select_another_room_btn', 'Select Another Room')}
                        </button>
                    </div>
                )}

                <div className={styles.formFooter}>
                    <p className={styles.footerText}>
                        <i className="fa fa-lock" />
                        {t('hotel_booking.secure_booking', 'Secure booking • Free cancellation • Best price guaranteed')}
                    </p>
                </div>
            </div>
        </div>,
        document.body
        )}
        </>
    );
};

export default BookingForm;
